import { useState, type ReactNode } from 'react';
import {
  Ban,
  Building2,
  CalendarCheck,
  CalendarClock,
  CalendarPlus,
  CalendarX2,
  ChevronDown,
  StickyNote,
  Users,
} from 'lucide-react';
import { type RequisitionDto } from '@eombor/contracts';
import { Badge, Skeleton, Table, cn, type Column } from '@eombor/ui';
import { t } from '../../../shared/i18n/index.js';
import { DESKTOP_QUERY, useMediaQuery } from '../../../shared/ui/useMediaQuery.js';
import { deadlineStateOf, requisitionStatusTone } from '../model/requisition-status.js';
import { requisitionStatusText } from '../../../shared/i18n/labels.js';

/**
 * Sana: "12.08.2026" — YAGONA formatlagich.
 *
 * Jadval ham, kartochka ham shu funksiyadan o'tadi: `neededBy` bir joyda
 * `07.09.2026`, boshqasida `2026-09-07` bo'lib chiqsa, odam ikkalasini
 * bir xil hujjatning ma'lumoti deb qabul qilmasdi. Vaqt ko'rsatilmaydi —
 * bu maydonlarning hammasi kun aniqligida.
 */
const formatDate = (iso: string | null): string | null => {
  if (iso === null) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const pad = (value: number): string => String(value).padStart(2, '0');
  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()}`;
};

export interface RequisitionTableProps {
  rows: readonly RequisitionDto[];
  onOpen?: (requisition: RequisitionDto) => void;
  /** Ombor nomi identifikator o'rniga — nomlar boshqa so'rovdan keladi. */
  warehouseNames?: ReadonlyMap<string, string>;
  /** Korxona bo'limi nomi. Topilmasa `null` — maydon umuman chizilmaydi. */
  orgUnitName?: (id: string | null) => string | null;
  caption: string;
  empty?: ReactNode;
  /** Yuklanmoqda — ko'rinishga MOS skelet chiziladi. */
  pending?: boolean;
}

/** Muddat holatiga qarab matn rangi — jadvalda ham, kartochkada ham bir xil. */
const deadlineClass = (state: ReturnType<typeof deadlineStateOf>): string | undefined =>
  state === 'overdue' ? 'text-danger' : state === 'today' ? 'text-warn' : undefined;

/**
 * Kartochkadagi bitta maydon.
 *
 * `wide` — qiymat uzun (ombor nomi, izoh): u ikkala ustunni egallaydi,
 * aks holda ikki tor ustunda so'zma-so'z sinib, o'qib bo'lmas holga
 * kelardi.
 */
interface MetaField {
  key: string;
  icon: ReactNode;
  label: string;
  value: ReactNode;
  wide?: boolean;
}

/**
 * Maydonlar ro'yxati — talabnomaning `RequisitionDto` dagi BARCHA
 * ma'noli qiymatlari.
 *
 * Bo'sh maydon ro'yxatga UMUMAN tushmaydi: "—" chizish ekranda joy
 * egallaydi-yu, hech narsa aytmaydi. Telefonda har qator qimmat.
 *
 * Ikkiga bo'linadi: `primary` — doim ko'rinadi, `rest` — «Batafsil»
 * ostida. Chegara ixtiyoriy emas: birinchi to'rttasi talabnomani
 * TANIB OLISH uchun yetadi (qayerdan, qachongacha, qachon so'ralgan,
 * kim olib ketadi), qolgani esa aynan bittasini ochib ko'rganda kerak.
 */
const metaFieldsOf = (
  row: RequisitionDto,
  warehouseName: string,
  orgUnit: string | null,
): { primary: MetaField[]; rest: MetaField[] } => {
  const deadline = deadlineStateOf(row.status, row.neededBy);
  const needed = formatDate(row.neededBy);
  const created = formatDate(row.createdAt);
  const ready = formatDate(row.readyAt);
  const closed = formatDate(row.closedAt);

  const primary: MetaField[] = [
    {
      key: 'warehouse',
      icon: <Building2 size={13} />,
      label: t('requisition.colWarehouse'),
      value: warehouseName,
      wide: true,
    },
  ];

  if (needed !== null) {
    primary.push({
      key: 'neededBy',
      icon: <CalendarClock size={13} />,
      label: t('requisition.colNeededBy'),
      value: (
        <span className={cn('flex flex-wrap items-center gap-1.5', deadlineClass(deadline))}>
          <span className="tabular">{needed}</span>
          {deadline === 'overdue' ? <Badge tone="danger">{t('requisition.overdue')}</Badge> : null}
          {deadline === 'today' ? <Badge tone="warn">{t('requisition.urgent')}</Badge> : null}
        </span>
      ),
    });
  }

  primary.push({
    key: 'createdAt',
    icon: <CalendarPlus size={13} />,
    label: t('requisition.fieldCreatedAt'),
    value: <span className="tabular">{created}</span>,
  });

  if (row.throughPerson !== null) {
    primary.push({
      key: 'throughPerson',
      icon: <Users size={13} />,
      label: t('requisition.fieldThroughPerson'),
      value: row.throughPerson,
    });
  }

  const rest: MetaField[] = [];

  if (orgUnit !== null) {
    rest.push({
      key: 'orgUnit',
      icon: <Building2 size={13} />,
      label: t('requisition.fieldOrgUnit'),
      value: orgUnit,
      wide: true,
    });
  }

  if (ready !== null) {
    rest.push({
      key: 'readyAt',
      icon: <CalendarCheck size={13} />,
      label: t('requisition.fieldReadyAt'),
      value: <span className="tabular">{ready}</span>,
    });
  }

  if (closed !== null) {
    rest.push({
      key: 'closedAt',
      icon: <CalendarX2 size={13} />,
      label: t('requisition.fieldClosedAt'),
      value: <span className="tabular">{closed}</span>,
    });
  }

  if (row.note !== null && row.note !== '') {
    rest.push({
      key: 'note',
      icon: <StickyNote size={13} />,
      label: t('requisition.fieldNote'),
      value: <span className="line-clamp-2 text-pretty">{row.note}</span>,
      wide: true,
    });
  }

  if (row.rejectReason !== null && row.rejectReason !== '') {
    rest.push({
      key: 'rejectReason',
      icon: <Ban size={13} />,
      label: t('requisition.rejectLabel'),
      value: <span className="line-clamp-2 text-pretty text-danger">{row.rejectReason}</span>,
      wide: true,
    });
  }

  return { primary, rest };
};

/**
 * Bitta maydon — `dt`/`dd` juftligi.
 *
 * Semantik ro'yxat ataylab: ekran o'qigich "Ombor: Toshkent markaziy
 * ombori" deb o'qiydi. Oddiy `span` lar bilan yozilsa, yorliq va qiymat
 * bir-biriga bog'lanmagan ikki matn bo'lib qolardi.
 */
const Meta = ({ icon, label, value, wide }: Omit<MetaField, 'key'>) => (
  <div className={cn('flex min-w-0 flex-col gap-0.5', wide === true ? 'col-span-2' : undefined)}>
    <dt className="flex items-center gap-1.5 text-[0.6875rem] leading-4 text-text-faint">
      <span aria-hidden className="shrink-0">
        {icon}
      </span>
      {label}
    </dt>
    {/* `break-words` — uzun ombor nomi yoki izoh kartochkadan CHIQIB
        ketmasin: gorizontal siljish aynan shundan paydo bo'lardi. */}
    <dd className="min-w-0 text-xs leading-5 break-words text-text-muted">{value}</dd>
  </div>
);

/**
 * Bitta talabnoma — TELEFON ko'rinishi.
 *
 * Butun kartochka bosiladi. Ichida alohida «Ochish» tugmasi bo'lsa,
 * barmoq uni topib bosishi kerak bo'lardi — holbuki kartochka bitta
 * narsani bildiradi va bitta joyga olib boradi.
 *
 * Texnik yechim: hujjat raqami tugma, uning `::after` i esa butun
 * kartochkani qoplaydi. Kartochkaning O'ZINI `<button>` qilib bo'lmaydi,
 * chunki ichida «Batafsil» tugmasi bor — tugma ichidagi tugma haqiqiy
 * HTML emas va bosilganda ikkala amal ham ishga tushardi.
 */
const RequisitionRowCard = ({
  row,
  warehouseName,
  orgUnit,
  onOpen,
}: {
  row: RequisitionDto;
  warehouseName: string;
  orgUnit: string | null;
  onOpen?: (requisition: RequisitionDto) => void;
}) => {
  const [expanded, setExpanded] = useState(false);
  const { primary, rest } = metaFieldsOf(row, warehouseName, orgUnit);
  const clickable = onOpen !== undefined;

  return (
    <li
      className={cn(
        'relative flex flex-col gap-2 rounded-[var(--radius-lg)] border border-border',
        'bg-surface p-3.5 shadow-[var(--shadow-1)]',
        clickable
          ? cn(
              'transition-[background-color,border-color] duration-[var(--motion-fast)]',
              'ease-[var(--ease-standard)] hover:border-accent/30 hover:bg-accent-soft/40',
              'has-[button:active]:bg-accent-soft/60 motion-reduce:transition-none',
            )
          : undefined,
      )}
    >
      <div className="flex items-center justify-between gap-2">
        {clickable ? (
          <button
            type="button"
            onClick={() => onOpen(row)}
            // Nom RAQAM bilan cheklanmaydi: ro'yxatdagi o'nta «TAL-2026-…»
            // bir-biridan faqat oxirgi raqami bilan farq qiladi va ekran
            // o'qigich bilan yurgan odam qaysi biri ekanini bilmasdi.
            aria-label={`${row.docNo} — ${row.purpose}`}
            className={cn(
              'tabular text-sm font-semibold text-text',
              "after:absolute after:inset-0 after:content-['']",
              'after:rounded-[var(--radius-lg)]',
            )}
          >
            {row.docNo}
          </button>
        ) : (
          <span className="tabular text-sm font-semibold text-text">{row.docNo}</span>
        )}
        <Badge tone={requisitionStatusTone(row.status)}>{requisitionStatusText(row.status)}</Badge>
      </div>

      {/* MAQSAD — kartochkaning asosiy matni: odam ro'yxatni aynan shu
          bo'yicha o'qiydi. Ikki qatordan uzunini qirqamiz, aks holda
          bitta uzun izoh butun ro'yxatni surib yuborardi. */}
      <p className="line-clamp-2 text-sm font-medium text-pretty text-text">{row.purpose}</p>

      <dl className="grid grid-cols-2 gap-x-3 gap-y-2">
        {primary.map(({ key, ...field }) => (
          <Meta key={key} {...field} />
        ))}
      </dl>

      {rest.length === 0 ? null : (
        <>
          {expanded ? (
            <dl className="grid grid-cols-2 gap-x-3 gap-y-2 border-t border-border pt-2">
              {rest.map(({ key, ...field }) => (
                <Meta key={key} {...field} />
              ))}
            </dl>
          ) : null}

          {/* `relative z-[1]` — yuqoridagi tugmaning butun kartochkani
              qoplagan `::after` i ostida qolib ketmasin, aks holda
              «Batafsil» bosilganda tafsilot sahifasi ochilardi. */}
          <button
            type="button"
            onClick={() => setExpanded((current) => !current)}
            aria-expanded={expanded}
            className={cn(
              'relative z-[1] flex items-center gap-1 self-start',
              'text-xs font-medium text-accent',
            )}
          >
            {expanded
              ? t('requisition.lessFields')
              : t('requisition.moreFields', { count: rest.length })}
            <ChevronDown
              size={14}
              aria-hidden
              className={cn(
                'transition-transform duration-[var(--motion-fast)]',
                'motion-reduce:transition-none',
                expanded ? 'rotate-180' : undefined,
              )}
            />
          </button>
        </>
      )}
    </li>
  );
};

/**
 * Talabnomalar ro'yxati — KENG ekranda jadval, telefonda kartochkalar.
 *
 * Ikkalasi bitta komponentda: sana formati va muddat qoidasi bir joyda
 * qoladi. Ikki alohida komponent bo'lsa, muddat belgisi bir ko'rinishda
 * qizil, boshqasida kulrang bo'lib ketardi.
 *
 * Tanlov CSS emas, JS (`useMediaQuery`): ikkala ko'rinishni ham chizib,
 * birini `hidden` bilan yashirish ekran o'qigichga ayni ro'yxatni IKKI
 * marta berardi. Ilovada bu qoida bitta (`useMobileNav` da ham shunday).
 *
 * Kartochkada jadvaldan KO'PROQ maydon bor va bu ataylab: jadvalda
 * o'ngga qarab joy bor, telefonda esa yo'q — shuning uchun jadval
 * ko'rsatmaydigan maydonlar («Batafsil» ostida) aynan shu yerda
 * ochiladi.
 */
export const RequisitionTable = ({
  rows,
  onOpen,
  warehouseNames,
  orgUnitName,
  caption,
  empty,
  pending = false,
}: RequisitionTableProps) => {
  const mobile = !useMediaQuery(DESKTOP_QUERY);
  const nameOf = (row: RequisitionDto): string =>
    warehouseNames?.get(row.warehouseId) ?? `#${row.warehouseId}`;

  if (mobile) {
    if (pending) {
      return (
        <div className="flex flex-col gap-2.5">
          {[0, 1, 2].map((key) => (
            <Skeleton key={key} className="h-[9.5rem] w-full rounded-[var(--radius-lg)]" />
          ))}
        </div>
      );
    }

    /* Bo'sh holat kartochkalar bilan bir sirtda turadi. Keng ekranda uni
       `Table` o'z katagiga soladi, shuning uchun o'ram AYNAN shu yerda. */
    if (rows.length === 0) {
      return empty === undefined ? null : (
        <div
          className={cn(
            'rounded-[var(--radius-lg)] border border-border bg-surface',
            'p-4 shadow-[var(--shadow-1)]',
          )}
        >
          {empty}
        </div>
      );
    }

    return (
      <ul aria-label={caption} className="flex flex-col gap-2.5">
        {rows.map((row) => (
          <RequisitionRowCard
            key={row.id}
            row={row}
            warehouseName={nameOf(row)}
            orgUnit={orgUnitName?.(row.requesterOrgUnitId) ?? null}
            {...(onOpen === undefined ? {} : { onOpen })}
          />
        ))}
      </ul>
    );
  }

  if (pending) return <Skeleton className="h-64 w-full" />;

  const columns: Column<RequisitionDto>[] = [
    { key: 'docNo', header: t('requisition.colDocNo'), mono: true, width: '11rem' },
    {
      key: 'status',
      header: t('requisition.colStatus'),
      width: '12rem',
      render: (row) => (
        <Badge tone={requisitionStatusTone(row.status)}>{requisitionStatusText(row.status)}</Badge>
      ),
    },
    {
      key: 'purpose',
      header: t('requisition.colPurpose'),
      render: (row) => <span className="block max-w-[28rem] truncate">{row.purpose}</span>,
    },
    /*
     * KORXONA — navbatdagi eng kerakli ustun.
     *
     * Ombor MTU ning markaziy ombori bo'lgani uchun butun MTU dagi
     * korxonalar unga yozadi: navbatdagi o'nta satrda ombor nomi bir xil
     * turadi va ularni ajratadigan yagona narsa — kim so'ragani.
     *
     * Ish joyi biriktirilmagan xodim yozgan hujjatda korxona yo'q va
     * katak «—» bo'lib qoladi: bu "biriktirilmagan" degani, xato emas.
     */
    {
      key: 'requesterOrgUnitId',
      header: t('requisition.colOrgUnit'),
      width: '10rem',
      render: (row) => {
        const unit = orgUnitName?.(row.requesterOrgUnitId) ?? null;
        return unit === null ? <span className="text-text-faint">—</span> : unit;
      },
    },
    {
      key: 'warehouseId',
      header: t('requisition.colWarehouse'),
      width: '14rem',
      render: (row) => nameOf(row),
    },
    {
      key: 'neededBy',
      header: t('requisition.colNeededBy'),
      align: 'right',
      mono: true,
      width: '12rem',
      // Muddati o'tgan talabnoma — ro'yxatdagi YAGONA qizil element. Shuning
      // uchun u ko'zga birinchi tashlanadi va yorliq bilan ham aytiladi:
      // rangni ko'rmaydigan odam ham bilishi kerak.
      render: (row) => {
        /* Bugungi sana QIZIL emas, sariq: u o'tmagan — bugun ulgurish
           kerak. Qizil "kechikdingiz" deydi va ikkovi bir xil ish emas. */
        const deadline = deadlineStateOf(row.status, row.neededBy);
        const text = formatDate(row.neededBy);
        if (text === null) return <span className="text-text-faint">—</span>;
        return (
          <span className={cn('inline-flex items-center justify-end gap-2', deadlineClass(deadline))}>
            {deadline === 'overdue' ? (
              <Badge tone="danger">{t('requisition.overdue')}</Badge>
            ) : null}
            {deadline === 'today' ? <Badge tone="warn">{t('requisition.urgent')}</Badge> : null}
            {text}
          </span>
        );
      },
    },
  ];

  return (
    <Table
      caption={caption}
      columns={columns}
      rows={[...rows]}
      rowKey={(row) => row.id}
      density="cozy"
      empty={empty}
      {...(onOpen === undefined ? {} : { onRowClick: onOpen })}
    />
  );
};
